import { reloadWhenSafe } from '@core/app-version/reload-when-safe.js';
import { compareDeployedBuild } from '@core/app-version/version-check.js';

const RELOAD_AT_KEY = 'eco-platform.chunk-reload-at';
const RELOAD_WINDOW_MS = 10_000;

/**
 * Decide whether a chunk-load failure warrants a one-shot recovery reload.
 * Returns 'reload' at most once per RELOAD_WINDOW_MS: a failure that recurs
 * within the window means the chunk is genuinely unfetchable (offline /
 * permanently missing), so it returns 'skip' to let the error surface as a
 * crash instead of looping. Only a 'reload' records the timestamp — a 'skip'
 * must not, or the window would keep extending and never recover.
 *
 * `now` is injected (Date.now() at the call site) so the timing is testable.
 */
export function decideChunkRecovery(now: number): 'reload' | 'skip' {
  try {
    const stored = sessionStorage.getItem(RELOAD_AT_KEY);
    const last = stored === null ? null : Number(stored);
    if (last !== null && Number.isFinite(last) && now - last < RELOAD_WINDOW_MS) {
      return 'skip';
    }
    sessionStorage.setItem(RELOAD_AT_KEY, String(now));
    return 'reload';
  } catch {
    // sessionStorage unavailable (privacy mode) — can't track attempts, so
    // allow the reload. Storage-disabled AND a permanently-broken chunk is
    // doubly rare; prioritise recovery.
    return 'reload';
  }
}

/**
 * Why a code-split chunk failed to load, as far as `version.json` can tell:
 * - `stale-build` — the server serves a newer build; this tab's chunk is gone.
 * - `same-build` — the server still serves this build, so the chunk exists;
 *   the fetch itself failed (flaky network, a request cut off mid-flight).
 * - `server-unreachable` — `version.json` didn't answer either: the tab is
 *   effectively offline.
 * - `no-build-id` — this bundle has no build id (local dev); nothing to compare.
 */
type ChunkFailureCause =
  'stale-build' | 'same-build' | 'server-unreachable' | 'no-build-id';

export type ChunkLoadRecoveryOptions = {
  /** This bundle's build id (`VITE_SENTRY_RELEASE`). Absent → no classification. */
  buildId: string | undefined;
  reload?: () => void;
  fetchBuildId?: () => Promise<string | null>;
};

async function classifyChunkFailure(
  buildId: string | undefined,
  fetchBuildId: (() => Promise<string | null>) | undefined,
): Promise<{
  cause: ChunkFailureCause;
  remoteBuildId: string | null;
}> {
  if (!buildId) return { cause: 'no-build-id', remoteBuildId: null };
  const { verdict, remoteBuildId } = await compareDeployedBuild({
    currentBuildId: buildId,
    ...(fetchBuildId ? { fetchBuildId } : {}),
  });
  const cause: ChunkFailureCause =
    verdict === 'newer'
      ? 'stale-build'
      : verdict === 'same'
        ? 'same-build'
        : 'server-unreachable';
  return { cause, remoteBuildId };
}

const RECOVERY_MESSAGES: Record<ChunkFailureCause, string> = {
  'stale-build': '[chunk-reload] stale chunk after deploy; reloading once to recover',
  'same-build':
    '[chunk-reload] chunk fetch failed on the current build (transient network failure?); reloading once to retry',
  'server-unreachable':
    '[chunk-reload] chunk fetch failed and version.json is unreachable (offline?); not reloading',
  'no-build-id':
    '[chunk-reload] chunk fetch failed (no build id to compare against); reloading once to recover',
};

/**
 * Register the `vite:preloadError` recovery listener. Vite emits this event
 * when a dynamic import (a code-split route chunk) fails to load. On a
 * guard-approved failure we ask `version.json` why (see `ChunkFailureCause`),
 * warn with that cause (forwarded to Sentry as a log, so a post-deploy storm
 * and a flaky-network blip are distinguishable there), and — unless the server
 * is unreachable, where a reload would only land on the browser's offline page
 * and drop in-memory work — reload once the app has no save pending or in
 * flight (`reloadWhenSafe`), so the reload never runs into the `beforeunload`
 * guard's dialog.
 *
 * The event is deliberately NOT default-prevented. Preventing it makes Vite
 * resolve the failed `import()` with `undefined`, and every `lazy()` loader
 * then crashes on `.default` — a confusing TypeError rather than the real
 * fetch failure. Letting the rejection propagate means `AppErrorBoundary`
 * receives the chunk-load error itself and renders `NewVersionPanel` until the
 * reload lands (or, if the user keeps the page, as the way out).
 *
 * `reload` and `fetchBuildId` are injectable so tests never trigger real
 * navigation or network. Returns a cleanup fn (used by tests; the app
 * registers once for its lifetime).
 */
export function registerChunkLoadRecovery({
  buildId,
  reload = () => {
    window.location.reload();
  },
  fetchBuildId,
}: ChunkLoadRecoveryOptions): () => void {
  const handler = (event: Event): void => {
    const payload = (event as Event & { payload?: unknown }).payload;
    if (decideChunkRecovery(Date.now()) !== 'reload') {
      console.error(
        '[chunk-reload] chunk still unfetchable right after a reload; leaving it to the crash panel',
        payload,
      );
      return;
    }
    void classifyChunkFailure(buildId, fetchBuildId).then(({ cause, remoteBuildId }) => {
      console.warn(RECOVERY_MESSAGES[cause], {
        cause,
        buildId,
        remoteBuildId,
        error: payload,
      });
      if (cause === 'server-unreachable') return;
      reloadWhenSafe({ reload });
    });
  };
  window.addEventListener('vite:preloadError', handler);
  return () => {
    window.removeEventListener('vite:preloadError', handler);
  };
}
